import type { Plan } from '@/fixtures/plans';

export const MANAGE_SUBSCRIPTION_SCREEN_TEXT = {
  TITLE: 'Manage subscription',
  PLAN_LABEL: 'Plan',
  CHANGE_PLAN_BUTTON: 'Change plan',
  CHANGE_PLAN_PAUSED_TOOLTIP:
    'Plan changes are unavailable while your subscription is paused. Resume your subscription to change plan.',
  CHANGE_PLAN_PAUSE_PENDING_TOOLTIP:
    'Plan changes are unavailable while your subscription is due to pause.',
  CHANGE_PLAN_DOWNGRADE_PENDING_TOOLTIP:
    'A plan change is already scheduled. Cancel the upcoming change to pick a different one.',
  CHANGE_PLAN_BUSINESS_TOOLTIP: "Business plans can't be downgraded.",
  BILLING_PERIOD_LABEL: 'Billing period',
  /** Lead-in before the next billing date */
  NEXT_CHARGE_PREFIX: "You'll be charged on",
  /** Lead-in before the next billing date while paused, which is the resume date */
  PAUSED_NEXT_CHARGE_PREFIX: "Your pause ends and you'll next be charged on",
  /** Card label for a monthly billing cycle */
  MONTHLY_LABEL: 'Monthly',
  YEARLY_LABEL: 'Yearly',
  PAUSE_OR_CANCEL_LINK: 'Pause or cancel subscription',
  CANCEL_LINK: 'Cancel subscription',
  PAUSE_WARNING_HEADING: 'Your subscription is due to pause',
  PAUSE_WARNING_BEFORE_DATES: 'Your plan will pause from',
  PAUSED_WARNING_HEADING: 'Your subscription is paused',
  PAUSED_WARNING_BEFORE_DATE:
    'Changing the plan or billing settings requires an active subscription. Your pause ends',
  PAUSED_WARNING_AFTER_DATE: 'or when you resume your subscription.',
  CANCEL_PAUSE_LINK: 'Cancel upcoming pause',
  CANCELING_PAUSE_LINK: 'Removing pause...',
  RESUME_LINK: 'Resume subscription',
  RESUMING_LINK: 'Resuming...',
  RESUME_ERROR_TOAST:
    'Unable to cancel the upcoming pause at this time, please try again later.',
  DOWNGRADE_WARNING_HEADING: 'Your plan is due to change',
  DOWNGRADE_WARNING_BEFORE_PLAN: 'Your plan will switch to',
  /** Target plan label while pricing options are loading or don't match */
  DOWNGRADE_TARGET_PLAN_FALLBACK: 'a new plan',
  DOWNGRADE_WARNING_PERIOD_BEFORE_PERIOD: "You'll switch to",
  DOWNGRADE_WARNING_PERIOD_AFTER_PERIOD: 'billing',
  DOWNGRADE_WARNING_BEFORE_DATE: 'on',
  CANCEL_DOWNGRADE_LINK: 'Cancel upcoming change',
  CANCELING_DOWNGRADE_LINK: 'Removing change...',
  CANCEL_DOWNGRADE_ERROR_TOAST:
    'Unable to cancel the upcoming plan change at this time, please try again later.',
} as const;

export const SWITCH_TO_YEARLY_SCREEN_TEXT = {
  TITLE: 'Confirm billing period change',
} as const;

export const SWITCH_TO_MONTHLY_SCREEN_TEXT = {
  TITLE: 'Confirm billing period change',
} as const;

export const CHANGE_PLAN_SCREEN_TEXT = {
  TITLE: 'Change plan',
  CURRENT_BADGE: 'CURRENT',
  CONTINUE_BUTTON: 'Continue',
  DOWNGRADE_WARNING_HEADING: "What you'll lose",
  DOWNGRADE_WARNING_DESCRIPTIONS: {
    Creator: {
      Essentials:
        "You'll lose access to our video libraries (stock footage, motion graphics, and LUTs).",
    },
    Pro: {
      Essentials:
        "Your license will cover individuals only - no client work or ads. You'll also lose access to WAV files and our video libraries (stock footage, motion graphics, and LUTs).",
      Creator:
        "Your license won't cover client work or paid ads, and you'll lose access to 4K footage and WAV files.",
    },
    Business: {
      Essentials:
        "Your license will cover individuals only - no teams, company content, client work, or ads. You'll also lose access to WAV files and our video libraries (stock footage, motion graphics, and LUTs).",
      Creator:
        "Your license won't cover company content, brand content, client work, or paid ads, and you'll lose access to 4K footage and WAV files.",
      Pro: "Your license won't cover company content, brand content, or standard commercial projects for teams and organizations.",
    },
  } satisfies Partial<Record<string, Partial<Record<string, string>>>>,
} as const;

/** Shown by the plan and billing-period change screens when the proration preview fails */
export const PLAN_PREVIEW_ERROR_TEXT = {
  MESSAGE:
    "We're currently unable to preview this change. Please try again, or contact us if the problem persists.",
  RETRY_BUTTON: 'Try again',
  CONTACT_BUTTON: 'Contact us',
} as const;

export const CONFIRM_PLAN_CHANGE_SCREEN_TEXT = {
  TITLE: 'Confirm plan change',
  NEW_PLAN_LABEL: 'New plan',
  NEW_MONTHLY_PRICE_LABEL: 'New monthly price',
  NEW_YEARLY_PRICE_LABEL: 'New yearly price',
  UNUSED_TIME_LABEL: 'Unused time',
  ESTIMATED_TAX_LABEL: 'Estimated tax',
  /** Breakdown total row; the asterisk points at the fine-print footnote */
  DUE_TODAY_LABEL: 'Due today*',
  CONFIRM_BUTTON: 'Upgrade now',
  PROCESSING_BUTTON: 'Processing payment',
  DOWNGRADE_STARTS_LABEL: 'Starts',
  DOWNGRADE_CONFIRM_BUTTON: 'Confirm downgrade',
  DOWNGRADE_FINE_PRINT_BEFORE_PLAN: "You'll stay on your current Uppbeat",
  DOWNGRADE_FINE_PRINT_BEFORE_DATE: 'plan until',
  DOWNGRADE_FINE_PRINT_BEFORE_TARGET: '. Your plan then switches to Uppbeat',
  DOWNGRADE_FINE_PRINT_BEFORE_PRICE: 'and your card is charged',
  DOWNGRADE_FINE_PRINT_BEFORE_TERM: 'in full for a',
  DOWNGRADE_FINE_PRINT_AFTER_TERM: 'subscription. Nothing is charged today.',
  DOWNGRADE_PROCESSING_BUTTON: 'Scheduling downgrade',
  DOWNGRADE_SUCCESS_TOAST: 'Your plan change is scheduled',
  DOWNGRADE_ERROR_TOAST:
    "We couldn't schedule your downgrade. Please try again or contact support.",
} as const;

/**
 * Compact per-plan copy for the change-plan picker rows: the pink `license`
 * line and the grey `features` line. Bespoke to this screen — deliberately
 * shorter than the marketing copy from `usePricingCards`.
 */
export const CHANGE_PLAN_PLAN_COPY: Record<
  'Essentials' | 'Creator' | 'Pro' | 'Business',
  { license: string; features: string }
> = {
  Essentials: {
    license: 'License for individuals',
    features: 'Unlimited Music & Sound effects',
  },
  Creator: {
    license: 'License for individuals',
    features: 'Full library + 1080p footage',
  },
  Pro: {
    license: 'Digital license for individuals - client work & ads',
    features: 'Full library + 4K footage & WAV audio',
  },
  Business: {
    license: 'Commercial digital license for organizations and teams',
    features: 'Full library + 4K footage & WAV audio',
  },
} as const;

const ALL_LIBRARIES_RESUME_FEATURES = [
  'Unlimited downloads on all libraries',
  'Safelist management - add, swap or update channels',
] as const;

// Free users can't have a paused subscription, so that entry is never shown.
export const RESUME_FEATURES: Record<Plan['name'], readonly string[]> = {
  Free: ALL_LIBRARIES_RESUME_FEATURES,
  Essentials: [
    'Unlimited downloads on Music & Sound effects',
    'Safelist management - add, swap or update your channel',
  ],
  Creator: ALL_LIBRARIES_RESUME_FEATURES,
  Pro: ALL_LIBRARIES_RESUME_FEATURES,
  Business: ALL_LIBRARIES_RESUME_FEATURES,
};

export const RESUME_CONFIRM_FEATURES = [
  'Downloads are resumed right away',
  'You can make changes to your safelist',
] as const;

export const RESUME_SUBSCRIPTION_SCREEN_TEXT = {
  TITLE: 'Confirm resume subscription',
  CONFIRMATION_HEADING: 'What happens after confirmation...',
  BILLING_NOTE_BEFORE_PRICE: "You'll be charged",
  BILLING_NOTE_AFTER_PRICE: 'now, then your next billing date will be',
  NOT_NOW_BUTTON: 'Not now',
  CONFIRM_RESUME_BUTTON: 'Confirm resume',
  RESUMING_BUTTON: 'Resuming...',
  RESUME_ERROR_TOAST:
    'Unable to resume your subscription at this time, please try again later.',
} as const;

export const RESUME_CONFIRMATION_SCREEN_TEXT = {
  TITLE: 'Subscription resumed',
  SUBTITLE_BEFORE_PLAN: 'Welcome back to your',
  SUBTITLE_AFTER_PLAN: 'features!',
  DESCRIPTION:
    "Your subscription is active again. Here's what you can do right now:",
  EXPLORE_BUTTON: 'Explore Uppbeat',
} as const;

export const PAUSE_OR_CANCEL_SCREEN_TEXT = {
  TITLE: 'Pause or cancel',
  RECOMMENDED_LABEL: 'RECOMMENDED',
  PAUSE_HEADING: 'Pause subscription',
  PAUSE_DESCRIPTION_BEFORE_DURATION: 'Pauses your next charge for',
  PAUSE_DESCRIPTION_AFTER_DURATION:
    'Your subscription stays active until then, and your safelist stays active throughout the pause.',
  PAUSE_DURATION_UNIT: 'months',
  PAUSE_DURATION_UNIT_SINGULAR: 'month',
  PAUSE_BENEFIT_COPYRIGHT: 'Copyright protection stays active',
  PAUSE_BENEFIT_RESUME: 'Resume anytime, no disruption',
  PAUSE_BENEFIT_BROWSE: 'Keep browsing and saving to Boards',
  PAUSE_BUTTON: 'Pause subscription',
  CANCEL_HEADING: 'Cancel subscription',
  CANCEL_DESCRIPTION:
    'Stops access to the library and safelisting\nat the end of your plan.',
  CANCEL_LINK: 'Cancel instead',
} as const;

export const PAUSING_MEANS_SCREEN_TEXT = {
  TITLE: 'Pause subscription',
  HEADING: 'Pausing means...',
  DESCRIPTION:
    'Your account stays protected while you take a break from creating.',
  ACCOUNT_STATE_HEADING:
    'While paused, this will be the state of your account:',
  /** Announced before the lost item; the icon swap alone isn't accessible. */
  ACCOUNT_STATE_UNAVAILABLE_LABEL: 'Not available while paused:',
  // Last item is rendered as the one that is lost
  ACCOUNT_STATE_FEATURES: [
    'Copyright protection on all your content',
    'Your safelisted channels',
    'Navigate the entire library',
    'Access to Boards, Customize Duration, and Sync Preview',
    'New downloads (available when you resume)',
  ],
  BILLING_NOTE_BEFORE_PRICE:
    "You won't be charged during the pause. Your next bill of",
  BILLING_NOTE_AFTER_PRICE: 'is charged when you resume.',
  KEEP_PLAN_BUTTON: 'Keep my plan active',
  NEXT_BUTTON: 'Next',
} as const;

export const PAUSE_SAFELIST_CHANNELS_SCREEN_TEXT = {
  HEADING: 'Your channels while paused',
  /** Section heading for Essentials, whose plan allows a single channel */
  HEADING_SINGULAR: 'Your channel while paused',
  DESCRIPTION:
    'Your safelist stays active during the pause and your channels protected.',
  /** Description for Essentials, whose plan allows a single channel */
  DESCRIPTION_SINGULAR:
    'Your safelist stays active during the pause and your channel protected.',
  THUMBNAIL_ALT: 'Safelisted Channel Thumbnail',
  CHANGE_LINK: 'Change',
  LOCKED_HEADING: 'Your safelist will be locked during the pause',
  LOCKED_NOTE_BEFORE_DATE:
    "You won't be able to add or change channels then. Make any updates before",
  LOCKED_NOTE_BEFORE_DATE_MOBILE:
    "You won't be able to add or change channels then.",
  LOCKED_NOTE_BEFORE_DATE_SINGULAR:
    "You won't be able to add or change your channel then. Make any updates before",
  LOCKED_NOTE_BEFORE_DATE_MOBILE_SINGULAR:
    "You won't be able to add or change your channel then.",
  CONFIRM_PAUSE_BUTTON: 'Confirm pause',
  PAUSE_ERROR_TOAST:
    'Unable to pause your subscription at this time, please try again later.',
} as const;

export const PAUSE_DURATION_SCREEN_TEXT = {
  TITLE: 'Pause subscription',
  NEXT_CHARGE_HEADING: 'Your next charge',
  PAUSE_EFFECT_BEFORE_DATE: 'Your pause will take effect on',
  PAUSE_EFFECT_AFTER_DATE: '- at the end of your current billing period.',
  PAUSING_FOR_BEFORE_VALUE: 'Pausing for',
  PAUSING_FOR_AFTER_VALUE: 'months',
  PAUSING_FOR_AFTER_VALUE_SINGULAR: 'month',
  PAUSE_PERIOD_LABEL: 'Pause period',
  CURRENT_BILLING_DATE_LABEL: 'Current billing date',
  NEW_BILLING_DATE_LABEL: 'New billing date',
  DATE_FALLBACK: 'N/A',
  KEEP_PLAN_BUTTON: 'Keep my plan active',
  NEXT_BUTTON: 'Next',
} as const;

export const PAUSE_DOWNLOAD_WARNING_SCREEN_TEXT = {
  TITLE: 'Resume your subscription to download',
  BODY_PREMIUM_BEFORE_TYPE:
    'Your subscription is currently paused. Resume your plan to unlock this premium',
  BODY_PREMIUM_AFTER_TYPE: 'and get back to creating.',
  BODY_LINE_1: 'Your subscription is currently paused.',
  BODY_LINE_2: 'Resume to unlock unlimited downloads.',
  NOT_NOW_BUTTON: 'Not Now',
  RESUME_BUTTON: 'Resume Subscription',
} as const;

/**
 * Shared layout for every cancellation screen inside the centered DialogStage
 * shell. Full-page on the mobile fullscreen surface (`min-h-[calc(100dvh-56px)]`,
 * below the 56px site header) so the button row pins to the bottom via each
 * screen's inner `grow`/`justify-end`. At/above the 600px surface-flip
 * breakpoint the min-height is dropped so the desktop modal sizes to its
 * content. Top padding leaves room for the header row / the stage's close
 * button in the top corners.
 */
export const CANCEL_SCREEN_CLASS =
  'flex min-h-[calc(100dvh-56px)] flex-col px-7 pt-6 md:p-8 [@media(min-width:600px)]:min-h-0 pb-10';

/**
 * Variant of {@link CANCEL_SCREEN_CLASS} for the resume screens, which use a
 * bottom drawer on mobile instead of the fullscreen surface — the full-page
 * min-height is dropped so the drawer sizes to its content.
 */
export const RESUME_SCREEN_CLASS =
  'flex flex-col px-7 pt-6 pb-10 md:px-container md:py-8 md:pb-6';

/**
 * Desktop height every mid-flow pause / cancellation step is pinned to, so the
 * surface stops resizing as you move between them (DEV-6799). 660px is the
 * tallest step as designed (`pause-or-cancel`, 647px), clamped to the same
 * 85dvh the surface caps itself at so short windows compress rather than
 * overflow — hence both classes living here, together.
 *
 * Applied via {@link CancelStep}; the terminal confirmation screens use a bare
 * {@link CANCEL_SCREEN_CLASS} div and keep sizing to their own content. Mobile
 * is already full-page, so neither class touches it.
 */
export const CANCEL_STEP_HEIGHT_CLASS =
  '[@media(min-width:600px)]:h-[min(660px,85dvh)] [@media(min-width:600px)]:overflow-y-auto';

/** Surface cap for every cancellation scene. Shares the 85dvh above. */
export const CANCELLATION_SURFACE_MAX_HEIGHT_CLASS = 'max-h-[85dvh]';

/**
 * Definite mobile height for the steps whose content scrolls inside a
 * `ScrollArea` — without it the viewport's `h-full` has nothing to resolve
 * against. Desktop gets its height from {@link CANCEL_STEP_HEIGHT_CLASS}.
 * Pass it to {@link CancelStep} alongside the shared layout.
 */
export const CANCEL_SCREEN_SCROLL_HEIGHT_CLASS = 'h-[calc(100dvh-56px)]';

export const CANCEL_SCROLL_FADE_CLASS =
  '[--fade-away-from-dark:var(--color-gray-950)] [@media(min-width:600px)]:[--fade-away-from-dark:var(--color-gray-875)]';

export const RETENTION_OFFER_MONTHLY_DURATION = '3 months';

export const RETENTION_OFFER_MONTHLY_TEXT = `${RETENTION_OFFER_MONTHLY_DURATION}, for only:`;

export const OFFER_SCREEN_TEXT = {
  CURRENT_PRICING_HEADING: "What you're paying now",
  SPECIAL_OFFER_HEADING: 'Special offer',
  YEARLY_OFFER_TEXT: 'Your next yearly payment, for only:',
  MODAL_DESCRIPTION:
    'Continue creating with unlimited downloads and copyright protection.',
  CLAIM_OFFER_BUTTON: 'Claim limited offer',
  CLAIMING_OFFER_BUTTON: 'Applying offer...',
  DECLINE_OFFER_BUTTON: 'No thanks',
  DECLINE_OFFER_EXTENDED_TEXT: 'continue to cancel',
} as const;

export const OFFER_CLAIMED_SCREEN_TEXT = {
  TITLE: 'Special offer applied!',
  SUCCESS_MESSAGE: "You're all set to keep creating without interruption.",
  DONE_BUTTON: 'Done',
} as const;

export const CONFIRM_CANCEL_SCREEN_TEXT = {
  TITLE: 'Canceling means...',
  DESCRIPTION:
    'Your account will be on a free plan at the end of your billing period.',
  DESCRIPTION_PAUSED:
    "Your paused subscription will end, and you'll be switched to our free plan.",
  /** Heading for the list of features user will lose */
  LOSE_ACCESS_HEADING: "As a free user, you'll lose access to:",
  KEEP_SUBSCRIPTION_BUTTON: 'Keep subscription',
  CONFIRM_CANCEL_BUTTON: 'Confirm cancellation',
} as const;

export const CANCELED_SCREEN_TEXT = {
  TITLE: 'Until next time!',
  CANCELED_MESSAGE: 'Your subscription has been canceled.',
  WELCOME_BACK_MESSAGE:
    "We're sorry to see you go, but you're always welcome back! You can reactivate your plan anytime.",
  WHAT_TO_EXPECT: "Here's what to expect from now:",
  END_DATE_HEADING: 'Subscription end date',
  DOWNGRADE_HEADING: 'Plan downgrade',
  COPYRIGHT_HEADING: 'Copyright claim protection',
  DONE_BUTTON: 'Done',
} as const;
